import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SuiteDraftContext, SuiteIdentityContext, type SuiteDraft } from './SuiteDraftContext.ts';

// Identity changes remount the store before any previous principal's draft is rendered.
export default function SuiteDraftProvider({
  identity,
  children,
}: {
  identity: string;
  children: ReactNode;
}) {
  return (
    <SuiteIdentityContext.Provider value={identity}>
      <DraftStore key={identity}>{children}</DraftStore>
    </SuiteIdentityContext.Provider>
  );
}

function DraftStore({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<Record<string, SuiteDraft>>({});
  const clear = useCallback(
    (key: string) =>
      setDrafts((previous) => {
        if (!(key in previous)) return previous;
        const next = { ...previous };
        delete next[key];
        return next;
      }),
    [],
  );
  const clearSuite = useCallback((orgId: string | undefined, suiteId: string) => {
    setDrafts((previous) => {
      // Denied responses may omit the owner project. Suite IDs still identify
      // the affected drafts within this authenticated organization.
      const keys = Object.keys(previous).filter((key) => {
        const [draftOrg, , draftSuite] = JSON.parse(key);
        return draftOrg === (orgId ?? null) && draftSuite === suiteId;
      });
      if (!keys.length) return previous;
      const next = { ...previous };
      for (const key of keys) delete next[key];
      return next;
    });
  }, []);
  const put = useCallback(
    (key: string, draft: SuiteDraft) =>
      setDrafts((previous) => {
        if (draft.body === draft.originalBody) {
          if (!(key in previous)) return previous;
          const next = { ...previous };
          delete next[key];
          return next;
        }
        return { ...previous, [key]: draft };
      }),
    [],
  );
  const clearMatching = useCallback(
    (key: string, draft: SuiteDraft) =>
      setDrafts((previous) => {
        const current = previous[key];
        if (
          !current ||
          current.body !== draft.body ||
          current.head !== draft.head ||
          current.revisionId !== draft.revisionId
        )
          return previous;
        const next = { ...previous };
        delete next[key];
        return next;
      }),
    [],
  );
  useEffect(() => {
    if (!Object.keys(drafts).length) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [drafts]);
  const value = useMemo(
    () => ({ drafts, clear, clearSuite, put, clearMatching }),
    [drafts, clear, clearSuite, put, clearMatching],
  );
  return <SuiteDraftContext.Provider value={value}>{children}</SuiteDraftContext.Provider>;
}
